import type { QueryClient } from '@tanstack/react-query';

import type { SharePointConnectionChoice } from '@/features/sharepoint/connection-choice.js';
import { findPlaceKnowledgeSource } from '@/features/sharepoint/place-index.js';
import { deleteOneDriveScope } from '@/lib/api/onedrive.js';
import {
  deleteSharePointPlace,
  type SharePointPlace,
} from '@/lib/api/sharepoint.js';
import {
  deleteKnowledgeSource,
  knowledgeSourceKeys,
  knowledgeSourcesQueryOptions,
  type KnowledgeSource,
} from '@/lib/api/workspace-knowledge.js';
import { notify } from '@/lib/notify.js';

/**
 * Takes a SharePoint place off a connection's list, and its own knowledge source off the shelf with
 * it.
 *
 * Adding a place registers both in one move, so removing has to undo both. SharePoint needs this
 * from the browser; OneDrive does not — see {@link removeFolderFromList}. Leaving the source
 * behind kept a place the agent could no longer reach indexed and searchable by every member who may
 * open its files in SharePoint — somebody who emptied the list found the Knowledge tab still full.
 * Every surface that removes a place goes through here, so none of them can forget the second half.
 *
 * The place goes first, because it is the access and the access is what was asked to go.
 */
export async function removePlaceFromList(
  queryClient: QueryClient,
  input: {
    workspaceId: string;
    connectionId: string;
    /** The workspace's SharePoint connections, which settle who owns a source with no connection id. */
    connections: readonly SharePointConnectionChoice[];
    placeId: string;
    places: readonly SharePointPlace[];
  },
): Promise<void> {
  const place = input.places.find(
    (candidate) => candidate.id === input.placeId,
  );
  await deleteSharePointPlace(input.placeId, input.workspaceId);
  if (!place) return;
  await dropOwnSource(
    queryClient,
    input.workspaceId,
    place.alias ?? place.nameSnapshot,
    (sources) =>
      findPlaceKnowledgeSource(
        place,
        sources,
        input.connectionId,
        input.connections,
      ),
  );
}

/**
 * The OneDrive half of {@link removePlaceFromList}. Nothing in the drive itself is touched.
 *
 * The shelf is not touched from here either, and that is not an omission: deleting a scope makes the
 * server withdraw every source the scope granted — its root and anything registered inside it (canon
 * §8). Repeating that from the browser could only ever remove less, and would race the server doing
 * it properly. What is left here is making the Knowledge tab read the result.
 */
export async function removeFolderFromList(
  queryClient: QueryClient,
  input: { workspaceId: string; scopeId: string },
): Promise<void> {
  await deleteOneDriveScope(input.scopeId, input.workspaceId);
  await queryClient.invalidateQueries({
    queryKey: knowledgeSourceKeys.list(input.workspaceId),
  });
}

/**
 * Takes one row's own source off the shelf, if it has one.
 *
 * The list is read through the query client rather than passed in: the Artifacts tabs never read
 * the knowledge list at all. It is always fetched fresh — a cached copy can predate the row's own
 * source, and deciding from it would leave that source indexed after the row is gone.
 *
 * A refusal is a warning, not a failure — the row is already off the list, so the access is gone
 * and only the index is stale. It says where to finish the job.
 */
async function dropOwnSource(
  queryClient: QueryClient,
  workspaceId: string,
  name: string,
  find: (sources: readonly KnowledgeSource[]) => KnowledgeSource | undefined,
): Promise<void> {
  try {
    const sources = await queryClient.fetchQuery({
      ...knowledgeSourcesQueryOptions(workspaceId),
      staleTime: 0,
    });
    const source = find(sources);
    if (source) await deleteKnowledgeSource(workspaceId, source.id);
  } catch {
    notify.warning(
      `“${name}” is off the list, but it may still be in the knowledge base. Remove it under Manage → Knowledge.`,
    );
  } finally {
    await queryClient.invalidateQueries({
      queryKey: knowledgeSourceKeys.list(workspaceId),
    });
  }
}
